import { marked } from "marked";
import DOMPurify from "dompurify";

// Custom extension: GitHub-style callouts.
//   > [!info] Title
//   > body line 1
//   > body line 2
// Renders as a coloured panel matching the design system.
//
// We rewrite the matched blockquote to a div with `.callout` classes BEFORE
// marked sees it, then mark it as raw HTML so marked passes it through.

const CALLOUT_RE = /^>\s*\[!(info|warning|todo|tip|note)\][^\n]*\n((?:>\s?.*\n?)*)/im;

function preprocessCallouts(md: string): string {
  let out = md;
  while (true) {
    const m = out.match(CALLOUT_RE);
    if (!m) break;
    const kind = m[1].toLowerCase();
    const rawBody = m[2]
      .split("\n")
      .map((l) => l.replace(/^>\s?/, ""))
      .join("\n")
      .trim();
    const variant =
      kind === "warning" ? "warning" : kind === "todo" || kind === "tip" ? "todo" : "info";
    const icon =
      variant === "warning" ? "⚠" : variant === "todo" ? "✓" : "ℹ";
    const inner = marked.parse(rawBody, { async: false }) as string;
    const html =
      `\n<div class="callout callout-${variant}">` +
      `<div class="callout-icon">${icon}</div>` +
      `<div class="callout-body">${inner}</div>` +
      `</div>\n\n`;
    // The callout regex absorbs the newline that terminated its last body line,
    // so we need to leave a blank line BEFORE the heading that follows.
    out = out.slice(0, m.index!) + html + out.slice(m.index! + m[0].length);
  }
  return out;
}

// Heading IDs for ToC anchors. We mutate the renderer once at module load.
const renderer = new marked.Renderer();
renderer.heading = ({ tokens, depth }) => {
  const text = tokens.map((t: any) => ("text" in t ? t.text : "")).join("");
  const id = slugify(text);
  return `<h${depth} id="${id}">${marked.parseInline(text, { async: false })}</h${depth}>\n`;
};

marked.setOptions({
  renderer,
  gfm: true,
  breaks: false,
});

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function renderMarkdown(md: string): string {
  const pre = preprocessCallouts(md);
  const html = marked.parse(pre, { async: false }) as string;
  // DOMPurify with iframes allowed for Drive / Loom / YouTube embeds.
  const clean = DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "loading", "referrerpolicy"],
  });
  return clean;
}

export interface TocEntry {
  level: number;
  text: string;
  id: string;
}

export function extractToc(md: string): TocEntry[] {
  const out: TocEntry[] = [];
  const lines = md.split("\n");
  let inFence = false;
  for (const line of lines) {
    if (line.startsWith("```")) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(/^(##|###)\s+(.+?)\s*$/);
    if (!m) continue;
    out.push({
      level: m[1].length,
      text: m[2].replace(/[*_`]/g, ""),
      id: slugify(m[2].replace(/[*_`]/g, "")),
    });
  }
  return out;
}
